/**
 * Raportimi i një dëmtimi ose incidenti.
 *
 * Ekziston pikërisht sepse dëmi NUK pret kthimin: një ekran i thyer në mes të
 * përdorimit duhet të hyjë në dosje atëherë kur ndodh, jo muaj më vonë kur
 * punonjësi largohet. Nëse pajisja është te dikush, incidenti lidhet vetvetiu me
 * atë caktim — përgjegjësia nuk kërkohet me dorë.
 *
 * Ndalesa ndjek të njëjtën rrugë si te kthimi: një rresht DRAFT te Rregullimet e
 * pagës, jo një zbritje e menjëhershme.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  MenuItem, Grid, Alert, Box, Typography, InputAdornment, Checkbox,
  FormControlLabel, CircularProgress,
} from '@mui/material';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import StatusChip from '../common/StatusChip';
import { INCIDENT_KINDS, assetTitle, statusChip, statusLabel } from '../../config/assets';

/** Ç'status merr pajisja nga një incident — pasqyrë e rrugës së serverit. */
function statusAfterIncident(kind, current) {
  if (kind === 'loss' || kind === 'theft') return 'lost';
  return current;
}

export default function IncidentDialog({ open, asset, assignment, onClose, onSave, today }) {
  const [form, setForm] = useState({
    incidentDate: '', kind: 'damage', description: '', cost: '', createDeduction: false,
  });
  const [error, setError] = useState('');
  const [busy, setBusy]   = useState(false);

  useEffect(() => {
    if (!open) return;
    setError('');
    setForm({
      incidentDate: today || new Date().toISOString().slice(0, 10),
      kind: 'damage', description: '', cost: '', createDeduction: false,
    });
  }, [open, today]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const nextStatus = useMemo(
    () => statusAfterIncident(form.kind, asset?.status || 'available'),
    [form.kind, asset],
  );

  // Pa punonjës nuk ka kujt t'i ndalet: kutia nuk ofrohet fare, në vend që të
  // ofrohet dhe të refuzohet nga serveri.
  const holder = assignment?.workerName || asset?.holder?.name || '';
  const canDeduct = !!(assignment?.workerId || asset?.holder?.workerId);
  const deductionInvalid = form.createDeduction && !(Number(form.cost) > 0);
  const invalid = !form.description.trim();

  async function submit() {
    if (invalid) { setError('Përshkrimi i incidentit është i detyrueshëm.'); return; }
    if (deductionInvalid) { setError('Ndalesa nga paga kërkon një kosto më të madhe se zero.'); return; }
    setBusy(true); setError('');
    try {
      await onSave({ ...form, cost: form.cost === '' ? null : Number(form.cost) });
    } catch (e) {
      setError(e.message || 'Regjistrimi dështoi.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <Box sx={{ width: 34, height: 34, borderRadius: 1.5, bgcolor: 'warning.soft', color: 'warning.onSoft',
                   display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <ReportProblemOutlinedIcon sx={{ fontSize: 19 }} />
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 800, fontSize: '1rem', lineHeight: 1.2 }}>Raporto dëmtim / incident</Typography>
          <Typography variant="caption" color="text.secondary">
            {asset ? `${asset.assetCode} · ${assetTitle(asset)}` : ''}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {!!error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
        {holder && (
          <Alert severity="info" sx={{ mb: 1.5 }}>
            Pajisja është te <strong>{holder}</strong> — incidenti lidhet me këtë dorëzim.
          </Alert>
        )}

        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={6}>
            <TextField type="date" label="Data e incidentit" fullWidth size="small"
              InputLabelProps={{ shrink: true }} value={form.incidentDate} onChange={set('incidentDate')} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField select label="Lloji" fullWidth size="small" value={form.kind} onChange={set('kind')}>
              {INCIDENT_KINDS.map((k) => <MenuItem key={k.key} value={k.key}>{k.label}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField label="Përshkrimi" required fullWidth size="small" multiline minRows={2}
              value={form.description} onChange={set('description')}
              placeholder="Ekrani i plasaritur në cepin e poshtëm djathtas…" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField type="number" label="Kosto e vlerësuar" fullWidth size="small"
              value={form.cost} onChange={set('cost')}
              InputProps={{ endAdornment: <InputAdornment position="end">{asset?.currency || 'EUR'}</InputAdornment> }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            {canDeduct ? (
              <>
                <FormControlLabel
                  sx={{ mt: 0.5 }}
                  control={<Checkbox checked={form.createDeduction}
                    onChange={(e) => setForm((f) => ({ ...f, createDeduction: e.target.checked }))} />}
                  label={<Typography variant="body2">Krijo ndalesë nga paga</Typography>}
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4, mt: -0.5 }}>
                  Shkon si draft te Rregullimet e pagës — pret aprovim.
                </Typography>
              </>
            ) : (
              <Typography variant="caption" color="text.secondary">
                Ndalesa kërkon një punonjës që e mban pajisjen.
              </Typography>
            )}
          </Grid>
        </Grid>

        {nextStatus !== asset?.status && (
          <Alert severity="warning" sx={{ mt: 1.5 }}>
            Pas regjistrimit pajisja bëhet{' '}
            <StatusChip status={statusChip(nextStatus)} label={statusLabel(nextStatus)} size="sm" />
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.5 }}>
        <Button onClick={onClose} disabled={busy} sx={{ textTransform: 'none' }}>Anulo</Button>
        <Button variant="contained" color="warning" onClick={submit} disabled={busy || invalid || deductionInvalid}
          startIcon={busy ? <CircularProgress size={15} color="inherit" /> : null}
          sx={{ textTransform: 'none', fontWeight: 700 }}>
          Regjistro incidentin
        </Button>
      </DialogActions>
    </Dialog>
  );
}
